import { cx } from "shared/utils/classnames";

import {
  useCallback,
  useRef,
  MutableRefObject,
  ChangeEvent,
  Dispatch,
  SetStateAction
} from "react";
import { toast } from "react-toastify";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { Button } from "shared/components/Button";
import { Show } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CloudUploadIcon,
  Delete01Icon,
  FileAttachmentIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Card } from "../Card";

const Preview = (props: { file: File; onDelete: () => void }) => {
  const { name } = props.file;
  return (
    <Card className="bg-white p-5 flex gap-3">
      <div className="size-10 flex items-center justify-center shrink-0 bg-primary-50 rounded-lg">
        <HugeiconsIcon
          size={24}
          icon={FileAttachmentIcon}
          className="text-primary-500 shrink-0"
        />
      </div>
      <div className="max-w-[calc(100%-100px)]">
        <p className="truncate text-sm font-bold text-content-1000">{name}</p>
        <p className="text-xs text-content-200">Uploaded</p>
      </div>
      <IconButton
        HugeIcon={Delete01Icon}
        className="text-danger-500 shrink-0 ml-auto"
        size={IconButtonSize.SM}
        onClick={() => {
          props.onDelete();
        }}
      />
    </Card>
  );
};

// Will be more reusable based on dev needs
export const DropZone = ({
  id,
  title,
  subText,
  value,
  accept,
  setValue,
  multiple,
  required
}: {
  id: string;
  title?: string;
  subText?: string;
  value?: File[];
  accept?: string;
  setValue: Dispatch<SetStateAction<File[]>>;
  multiple?: boolean;
  required?: boolean;
}) => {
  const uploader = useRef<HTMLInputElement>();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0)
        if (Array.from(e.target.files).some((f) => f.size > 10485760))
          toast.error("File size should be less than 10MB");
        else setValue((prev) => [...prev, ...Array.from(e.target.files || [])]);
      else setValue([]);
    },
    [setValue]
  );

  return (
    <div>
      {value?.length ? (
        <>
          <div className="space-y-2">
            {Array.from(value).map((f, idx) => (
              <Preview
                key={idx + f.name + f.size}
                file={f}
                onDelete={() => {
                  if (uploader.current?.value) uploader.current.value = "";

                  setValue(Array.from(value).filter((_, i) => i !== idx));
                }}
              />
            ))}
          </div>
          <Button
            className="w-full mt-3"
            onClick={() => uploader.current?.click()}
            HugeIcon={CloudUploadIcon}
          >
            Upload more
          </Button>
        </>
      ) : null}
      <label
        className={cx(
          "animate-appear relative h-auto w-full cursor-pointer rounded-lg border border-dashed border-primary-500 bg-primary-20 hover:bg-primary-50 block",
          value?.length && "hidden"
        )}
        htmlFor={id}
      >
        <div className="space-y-2 py-6">
          <HugeiconsIcon
            icon={CloudUploadIcon}
            className="text-primary-500 mx-auto"
            size={24}
            strokeWidth={2}
          />
          <p className="text-sm font-bold text-primary-500 text-center">
            {title || "Upload image or document"}
          </p>
          <Show if={subText}>
            <p className="text-xs font-medium text-primary-500 text-center">
              {subText}
            </p>
          </Show>
        </div>

        <input
          ref={uploader as MutableRefObject<HTMLInputElement>}
          accept={accept}
          className="absolute left-0 h-[140px] w-full opacity-0"
          id={id}
          multiple={multiple}
          onChange={handleChange}
          required={required}
          type="file"
        />
      </label>
    </div>
  );
};
